import { useMemo, useState } from 'react';
import { Modal } from '../../../../components/Modal';
import { useParamsContext } from '../../../../contexts/ParamsContext';
import { useAppContext } from '../../../../contexts/AppContext';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { dayLabels, Horarios, initialHorarios, minutesToTime, timeToMinutes } from '../../Enum';
import { EstabelecimentosService } from '../../../estabelecimentos/system-admin/Service';

const estabsService = new EstabelecimentosService();

const defaultCommercialHorarios: Horarios = {
    segunda: [{ start: 9 * 60, end: 18 * 60 }],
    terca: [{ start: 9 * 60, end: 18 * 60 }],
    quarta: [{ start: 9 * 60, end: 18 * 60 }],
    quinta: [{ start: 9 * 60, end: 18 * 60 }],
    sexta: [{ start: 9 * 60, end: 18 * 60 }],
    sabado: [{ start: 9 * 60, end: 13 * 60 }],
    domingo: [],
};

const hasAnySlot = (horarios: Horarios) =>
    Object.values(horarios).some((slots) => (slots || []).length > 0);

const normalizeHorarios = (horarios?: any): Horarios => {
    const merged = {
        ...initialHorarios,
        ...(horarios || {}),
    };
    const result: Horarios = {};
    Object.keys(merged).forEach((day) => {
        result[day] = (merged[day] || []).map((slot: any) => ({
            start: Number(slot?.start || 0),
            end: Number(slot?.end || 0),
        }));
    });

    if (!hasAnySlot(result)) {
        return { ...defaultCommercialHorarios };
    }

    return result;
};

const validateHorarios = (horarios: Horarios): Horarios => {
    const validated: Horarios = {};
    Object.entries(horarios).forEach(([day, slots]) => {
        validated[day] = (slots || []).filter((slot) => slot.start >= 0 && slot.end > slot.start);
    });
    return validated;
};

export default function QuickHorariosModal() {
    const { clear } = useParamsContext();
    const { successSnackbar, errorSnackbar } = useAppContext();
    const { estabSession, reloadEstab } = useEstabsContext();
    const [loading, setLoading] = useState(false);
    const [horarios, setHorarios] = useState<Horarios>(
        normalizeHorarios((estabSession as any)?.agenda?.horarios),
    );

    const orderedDays = useMemo(
        () => ['segunda', 'terca', 'quarta', 'quinta', 'sexta', 'sabado', 'domingo'],
        [],
    );

    const addSlot = (day: string) => {
        setHorarios((prev) => ({
            ...prev,
            [day]: [...(prev[day] || []), { start: 0, end: 0 }],
        }));
    };

    const removeSlot = (day: string, index: number) => {
        setHorarios((prev) => ({
            ...prev,
            [day]: (prev[day] || []).filter((_, i) => i !== index),
        }));
    };

    const changeSlot = (day: string, index: number, field: 'start' | 'end', value: string) => {
        setHorarios((prev) => ({
            ...prev,
            [day]: (prev[day] || []).map((slot, i) => (i === index ? { ...slot, [field]: timeToMinutes(value) } : slot)),
        }));
    };

    const submit = async () => {
        const estabId = (estabSession as any)?._id;
        if (!estabId) {
            errorSnackbar('Estabelecimento nao encontrado para atualizar horarios.');
            return;
        }

        const validated = validateHorarios(horarios);

        setLoading(true);
        try {
            await estabsService.update(estabId, {
                'agenda.horarios': validated,
                onboarding: {
                    ...((estabSession as any)?.onboarding || {}),
                    steps: {
                        ...((estabSession as any)?.onboarding?.steps || {}),
                        agenda: true,
                    },
                },
            });
            await reloadEstab();
            successSnackbar('Horarios salvos com sucesso!');
            clear();
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Nao foi possivel salvar os horarios.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            title="Configuracao rapida de horarios"
            subTitle="Defina os horarios principais da semana. Ao salvar, o passo de agenda no onboarding sera concluido."
            onClose={clear}
        >
            <div className="space-y-4">
                <div className="rounded-lg border border-base-300 bg-base-200/60 px-3 py-2 text-xs text-base-content/70">
                    Quando nao houver agenda configurada, preenchemos automaticamente um horario comercial padrao
                    (segunda a sexta 09:00-18:00 e sabado 09:00-13:00).
                </div>

                <div className="max-h-[55vh] overflow-y-auto pr-1 space-y-3">
                    {orderedDays.map((day) => (
                        <div key={day} className="rounded-xl border border-base-300 bg-base-100 p-3">
                            <div className="flex items-center justify-between mb-2">
                                <div className="text-sm font-semibold text-base-content">{dayLabels[day] || day}</div>
                                <button
                                    type="button"
                                    className="btn btn-outline btn-xs"
                                    onClick={() => addSlot(day)}
                                    disabled={loading}
                                >
                                    Adicionar
                                </button>
                            </div>

                            {(horarios[day] || []).length === 0 && (
                                <div className="text-xs text-base-content/60">Sem intervalos neste dia.</div>
                            )}

                            {(horarios[day] || []).map((slot, index) => (
                                <div key={`${day}_${index}`} className="flex items-center gap-2 mb-2">
                                    <input
                                        type="time"
                                        className="input input-bordered input-sm w-28"
                                        value={minutesToTime(slot.start)}
                                        onChange={(e) => changeSlot(day, index, 'start', e.target.value)}
                                        disabled={loading}
                                    />
                                    <span className="text-xs text-base-content/60">ate</span>
                                    <input
                                        type="time"
                                        className="input input-bordered input-sm w-28"
                                        value={minutesToTime(slot.end)}
                                        onChange={(e) => changeSlot(day, index, 'end', e.target.value)}
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-xs text-error"
                                        onClick={() => removeSlot(day, index)}
                                        disabled={loading}
                                    >
                                        Remover
                                    </button>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>

                <div className="flex justify-end gap-2">
                    <button type="button" className="btn btn-ghost" onClick={clear} disabled={loading}>
                        Cancelar
                    </button>
                    <button type="button" className="btn btn-primary" onClick={submit} disabled={loading}>
                        {loading ? 'Salvando...' : 'Salvar horarios'}
                    </button>
                </div>
            </div>
        </Modal>
    );
}
